import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function AdminPanel() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState('');
  const [filter, setFilter] = useState({ type: 'all', status: 'all' });
  const [viewing, setViewing] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleLogin = async () => {
    if (!adminKey.trim()) {
      setAuthError('Please enter an admin key');
      return;
    }

    setLoading(true);
    setAuthError('');
    
    try {
      const response = await fetch('/api/get-submissions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ adminKey: adminKey.trim() })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setIsAuthenticated(true);
        setSubmissions(result.submissions);
      } else {
        setAuthError('Invalid admin key');
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Authentication error:', error);
      setAuthError('Authentication failed. Please try again.');
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/get-submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ adminKey, ...filter })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setSubmissions(result.submissions);
      } else {
        if (result.error === 'Unauthorized') {
          setIsAuthenticated(false);
          setAuthError('Session expired. Please login again.');
        }
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  }, [filter, adminKey]); // Dependencies for useCallback

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAdminKey('');
    setSubmissions([]);
    setAuthError('');
    setFilter({ type: 'all', status: 'all' });
  };

  const openView = async (submission) => {
    // refresh full details (including markdown/images) using admin key header
    try {
      const resp = await fetch(`/api/get-submission-details?id=${submission.id}`, {
        headers: { 'x-admin-key': adminKey }
      });
      const data = await resp.json();
      if (data.success) {
        setViewing({ ...submission, ...data.submission });
      } else {
        setViewing(submission);
      }
    } catch {
      setViewing(submission);
    }
  };

  const closeView = () => setViewing(null);

  const saveEdits = async () => {
    if (!viewing) return;
    setSaving(true);
    try {
      const resp = await fetch('/api/update-submission', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminKey, submissionId: viewing.id, updates: viewing })
      });
      const data = await resp.json();
      if (data.success) {
        setViewing(data.submission);
        await fetchSubmissions();
        alert('Saved');
      } else {
        alert(data.error || 'Save failed');
      }
    } catch (e) {
      alert('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const approveSubmission = async (submissionId) => {
    try {
      const response = await fetch('/api/approve-submission', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          submissionId,
          adminKey
        }),
      });

      const result = await response.json();
      if (result.success) {
        alert('Submission approved successfully');
        fetchSubmissions();
      } else {
        alert('Failed to approve submission: ' + result.error);
      }
    } catch (error) {
      console.error('Error approving submission:', error);
      alert('Error approving submission');
    }
  };

  const rejectSubmission = async (submissionId) => {
    const reason = prompt('Reason for rejection (optional):');
    if (reason === null) return;
    
    try {
      const response = await fetch('/api/reject-submission', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          submissionId,
          adminKey,
          reason
        }),
      });

      const result = await response.json();
      if (result.success) {
        alert('Submission rejected successfully');
        fetchSubmissions();
      } else {
        alert('Failed to reject submission: ' + result.error);
      }
    } catch (error) {
      console.error('Error rejecting submission:', error);
      alert('Error rejecting submission');
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchSubmissions();
    }
  }, [filter, isAuthenticated, fetchSubmissions]); // Now includes fetchSubmissions

  return (
    <div className="admin-panel">
      <div className="container">
        {!isAuthenticated ? (
          <div className="admin-auth">
            <h2>Admin Login</h2>
            <div className="auth-form">
              <input
                type="password"
                placeholder="Enter Admin Key"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                className="admin-key-input"
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              />
              {authError && (
                <div className="auth-error">
                  {authError}
                </div>
              )}
              <button 
                onClick={handleLogin} 
                disabled={loading || !adminKey.trim()}
                className="login-button"
              >
                {loading ? 'Authenticating...' : 'Login'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="admin-header">
              <h2>Admin Panel</h2>
              <button onClick={handleLogout} className="logout-button">
                Logout
              </button>
            </div>

            <div className="admin-filters">
              <select
                value={filter.type}
                onChange={(e) => setFilter(prev => ({ ...prev, type: e.target.value || 'all' }))}
              >
                <option value="all">All Types</option>
                <option value="hack">Hacks</option>
                <option value="scammer">Scammers</option>
              </select>
              
              <select
                value={filter.status}
                onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value || 'all' }))}
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              
              <button onClick={fetchSubmissions} disabled={loading}>
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>

            <div className="submissions-list">
              {submissions.length === 0 ? (
                <p>No submissions found</p>
              ) : (
                submissions.map((submission) => (
                  <div key={submission.id} className="submission-card">
                    <div className="submission-header">
                      <h3>{submission.type === 'hack' ? submission.projectName : submission.username}</h3>
                      <span className={`status ${submission.status}`}>{submission.status}</span>
                    </div>
                    <p>{submission.description}</p>
                    <div className="submission-actions">
                      <button onClick={() => openView(submission)}>
                        View
                      </button>
                      <button onClick={() => approveSubmission(submission.id)}>
                        Approve
                      </button>
                      <button onClick={() => rejectSubmission(submission.id)}>
                        Reject
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {viewing && (
              <div className="modal-overlay" onClick={closeView}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h2>Submission Details</h2>
                    <button className="modal-close" onClick={closeView}>×</button>
                  </div>
                  <div className="contact-form">
                    <div className="form-group">
                      <label>Status</label>
                      <select
                        value={viewing.status}
                        onChange={(e) => setViewing(v => ({ ...v, status: e.target.value }))}
                      >
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>

                    {viewing.type === 'hack' ? (
                      <>
                        <div className="form-group">
                          <label>Project Name</label>
                          <input value={viewing.projectName || ''} onChange={(e) => setViewing(v => ({ ...v, projectName: e.target.value }))} />
                        </div>
                        <div className="form-group">
                          <label>Amount</label>
                          <input value={viewing.amount || ''} onChange={(e) => setViewing(v => ({ ...v, amount: e.target.value }))} />
                        </div>
                        <div className="form-group">
                          <label>Month</label>
                          <input value={viewing.month || ''} onChange={(e) => setViewing(v => ({ ...v, month: e.target.value }))} />
                        </div>
                        <div className="form-group">
                          <label>Year</label>
                          <input value={viewing.year || ''} onChange={(e) => setViewing(v => ({ ...v, year: e.target.value }))} />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="form-group">
                          <label>Platform</label>
                          <input value={viewing.platform || ''} onChange={(e) => setViewing(v => ({ ...v, platform: e.target.value }))} />
                        </div>
                        <div className="form-group">
                          <label>Username</label>
                          <input value={viewing.username || ''} onChange={(e) => setViewing(v => ({ ...v, username: e.target.value }))} />
                        </div>
                      </>
                    )}

                    <div className="form-group">
                      <label>Description</label>
                      <textarea rows="3" value={viewing.description || ''} onChange={(e) => setViewing(v => ({ ...v, description: e.target.value }))} />
                    </div>

                    <div className="form-group">
                      <label>Markdown</label>
                      <textarea rows="8" value={viewing.markdownContent || ''} onChange={(e) => setViewing(v => ({ ...v, markdownContent: e.target.value }))} />
                    </div>

                    {viewing.markdownContent && (
                      <div className="form-group">
                        <label>Preview</label>
                        <div className="markdown-content">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {viewing.markdownContent}
                          </ReactMarkdown>
                        </div>
                      </div>
                    )}

                    <div className="form-actions">
                      <button className="cta-button secondary" onClick={closeView}>Close</button>
                      <button className="cta-button" onClick={saveEdits} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AdminPanel;
